import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarPlus, Ticket, Trophy, Gift, BarChart3,
  Crown, Bell, User, LogOut, Menu, X, Zap, Star, ShieldCheck, MessagesSquare, Gauge, Car,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import apiClient from "@/lib/apiClient";
import logo from "@/assets/logo.png";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/booking", label: "Book Station", icon: CalendarPlus },
  { to: "/my-bookings", label: "My Bookings", icon: Ticket },
  { to: "/sessions", label: "Sim Sessions", icon: Gauge },
  { to: "/tournaments", label: "Tournaments", icon: Trophy },
  { to: "/rewards", label: "Rewards", icon: Gift },
  { to: "/leaderboards", label: "Leaderboards", icon: BarChart3 },
  { to: "/car-clubs", label: "Car Clubs", icon: Car },
  { to: "/community", label: "Community", icon: MessagesSquare },
  { to: "/membership", label: "Membership", icon: Crown },
];

const NavLinks = ({ onNavigate }) => (
  <nav className="flex flex-col gap-1.5">
    {navItems.map(({ to, label, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        onClick={onNavigate}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-4 py-3 rounded-xl font-semibold tracking-wide transition-all ${
            isActive
              ? "text-white"
              : "text-slate-400 hover:text-white hover:bg-white/5"
          }`
        }
        style={({ isActive }) =>
          isActive
            ? {
                background: "linear-gradient(90deg, rgba(0,229,255,0.16), rgba(139,92,246,0.12))",
                border: "1px solid rgba(0,229,255,0.35)",
                boxShadow: "inset 0 0 20px -8px rgba(0,229,255,0.5)",
              }
            : { border: "1px solid transparent" }
        }
      >
        <Icon className="w-5 h-5 shrink-0" />
        <span>{label}</span>
      </NavLink>
    ))}
  </nav>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showBalloon, setShowBalloon] = useState(false);
  const poppedRef = useRef(false);
  const prevCountRef = useRef(0);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    const fetchUnread = async () => {
      try {
        const { data } = await apiClient.get("/notifications/unread-count");
        if (!active) return;
        const count = data.unread_count || 0;
        setUnread(count);
        // Pop the balloon on first load with unread items, and again whenever a NEW
        // notification arrives while the user is already in-app (count increases).
        if (count > 0 && (!poppedRef.current || count > prevCountRef.current)) {
          poppedRef.current = true;
          setShowBalloon(true);
          setTimeout(() => active && setShowBalloon(false), 7000);
        }
        prevCountRef.current = count;
      } catch (e) { /* ignore */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 20000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const initials = user ? `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() : "";

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <button
        onClick={() => { navigate("/dashboard"); onNavigate?.(); }}
        className="flex items-center gap-2 px-2 py-4 mb-2"
        data-testid="sidebar-logo"
      >
        <img src={logo} alt="Pit Lane Lounge" className="h-11 w-auto object-contain" />
      </button>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-1">
        <NavLinks onNavigate={onNavigate} />
      </div>
      <div className="mt-4 pt-4 border-t border-white/10 px-1 space-y-1.5">
        {user?.role === "admin" && (
          <NavLink
            to="/admin"
            onClick={onNavigate}
            data-testid="nav-admin"
            className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all text-slate-300 hover:text-white"
            style={{ background: "rgba(255,46,151,0.1)", border: "1px solid rgba(255,46,151,0.3)" }}
          >
            <ShieldCheck className="w-5 h-5" style={{ color: "#ff2e97" }} /> Admin Console
          </NavLink>
        )}
        <NavLink
          to="/profile"
          onClick={onNavigate}
          data-testid="nav-profile"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all ${
              isActive ? "text-white bg-white/5" : "text-slate-400 hover:text-white hover:bg-white/5"
            }`
          }
        >
          <User className="w-5 h-5" /> Profile
        </NavLink>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-slate-400 hover:text-white hover:bg-red-500/10 transition-all"
        >
          <LogOut className="w-5 h-5" /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grid-overlay">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col glass border-r border-white/10 px-4 py-3 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[80%] glass border-r border-white/10 px-4 py-3 animate-in-up">
            <button
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
              onClick={() => setMobileOpen(false)}
              data-testid="close-mobile-menu"
            >
              <X className="w-6 h-6" />
            </button>
            <SidebarInner onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 glass border-b border-white/10">
          <div className="flex items-center justify-between px-4 sm:px-6 h-16">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden text-slate-300 hover:text-white"
                onClick={() => setMobileOpen(true)}
                data-testid="open-mobile-menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <img src={logo} alt="logo" className="h-8 w-auto object-contain lg:hidden" />
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <div
                className="chip"
                style={{ background: "rgba(255,176,32,0.12)", border: "1px solid rgba(255,176,32,0.4)", color: "#ffb020" }}
                data-testid="topbar-points"
              >
                <Zap className="w-3.5 h-3.5" />
                {(user?.points ?? 0).toLocaleString()} PTS
              </div>

              {user?.free_session_credits > 0 && (
                <div className="chip hidden sm:inline-flex"
                  style={{ background: "rgba(0,255,157,0.12)", border: "1px solid rgba(0,255,157,0.4)", color: "#00ff9d" }}>
                  <Star className="w-3.5 h-3.5" />
                  {user.free_session_credits} FREE HR
                </div>
              )}

              <button
                onClick={() => { setShowBalloon(false); navigate("/notifications"); }}
                className="relative text-slate-300 hover:text-white transition-colors"
                data-testid="notifications-bell"
              >
                <Bell className={`w-6 h-6 ${showBalloon && unread > 0 ? "bell-ring" : ""}`} />
                {unread > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center pulse-dot"
                    style={{ background: "#ff2e97", color: "#fff" }}
                    data-testid="unread-badge"
                  >
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
                {showBalloon && unread > 0 && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setShowBalloon(false); navigate("/notifications"); }}
                    className="absolute right-0 top-full mt-3 z-40 whitespace-nowrap rounded-xl px-3 py-2 text-xs font-semibold text-white shadow-xl balloon-pop"
                    style={{ background: "linear-gradient(90deg,#00e5ff,#8b5cf6)", boxShadow: "0 8px 30px -6px rgba(0,229,255,0.6)" }}
                    data-testid="unread-balloon"
                  >
                    <span className="absolute -top-1.5 right-3 w-3 h-3 rotate-45" style={{ background: "#00e5ff" }} />
                    🔔 You have {unread > 9 ? "9+" : unread} unread notification{unread === 1 ? "" : "s"}
                  </span>
                )}
              </button>

              <button
                onClick={() => navigate("/profile")}
                className="w-9 h-9 rounded-xl flex items-center justify-center font-display font-bold text-sm text-black"
                style={{ background: "linear-gradient(135deg,#00e5ff,#8b5cf6)" }}
                data-testid="profile-avatar"
              >
                {initials || <User className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-8 max-w-7xl mx-auto w-full">
          {children}
        </main>
      </div>
    </div>
  );
};
